import React, { useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";

import { getSupportedTransform } from "shared/util/Prefixes";

import "./SlidingPagination.scss";

// A helper to get the number of children to paginate. For some reason, the
// first child can be `null`, so using the simpler
// `React.Children.count(children)` returned a number one too high.
const countTruthyChildren = (children) => {
	return React.Children.toArray(children).filter((child) => !!child).length;
};

// Calculates the shiftWidth, innerWidth, and pageCount.
export const calculateSizes = (itemWidth, itemGutter, width, itemCount) => {
	// the actual width of a single page (this includes the extra margin on the
	// right side) - i.e., this is the amount to shift the page over
	let displayWidth = 0;

	// the number of times to loop when calculating the display width. we can
	// use the number of items that fits in one page (skipping gutters since
	// that will be larger than what will actually fit), then add one to ensure
	// that we get over the limit.
	const iterationCount = Math.ceil(width / itemWidth) + 1;

	Array.from(Array(iterationCount).keys()).forEach((i) => {
		const tmpWidth = itemWidth * i + itemGutter * (i - 1);

		if (tmpWidth <= width) {
			displayWidth = tmpWidth;
		}
	});

	const shiftWidth = displayWidth + itemGutter;

	// the total width of all items
	let innerWidth = itemWidth * itemCount + itemGutter * (itemCount - 1);

	// the number of pages (minus one to 0-index)
	const pageCount = Math.ceil(innerWidth / shiftWidth) - 1;

	// now that the number of pages has been calculated, make the inner width
	// be the witdh of the container, at a minimum
	if (innerWidth < width) {
		innerWidth = width;
	}

	return {
		shiftWidth,
		innerWidth,
		pageCount,
	};
};

const SlidingPagination = ({
	width,
	fadeOutSides,
	disabled,
	children,
	previousButtonText,
	nextButtonText,
	onPrevious,
	onNext,
	page: defaultPage,
	itemWidth,
	itemGutter,
}) => {
	// Count number of children.
	const itemCount = countTruthyChildren(children);

	// Calculate (and memoize) the styles for this component.
	const {
		shiftWidth,
		innerWidth,
		pageCount,
	} = useMemo(
		() => calculateSizes(itemWidth, itemGutter, width, itemCount),
		[itemWidth, itemGutter, width, itemCount],
	);

	// Getters/setters for the current page.
	const [page, setPage] = useState(defaultPage);

	// Event handlers.
	const handlePrevious = useCallback(() => {
		const nextPage = Math.max(page - 1, 0);
		setPage(nextPage);

		if (typeof onPrevious === "function") {
			onPrevious(nextPage);
		}
	}, [page, setPage, onPrevious]);
	const handleNext = useCallback(() => {
		const nextPage = Math.min(page + 1, pageCount);
		setPage(nextPage);

		if (typeof onNext === "function") {
			onNext(nextPage);
		}
	}, [page, setPage, onNext, pageCount]);

	// Get the transform property for this browser.
	const transformProperty = getSupportedTransform();
	const innerStyles = {};
	innerStyles.width = innerWidth;
	innerStyles[transformProperty] = `translateX(${shiftWidth * page * -1}px)`;

	// Should we show pagination buttons/fades?
	const hasPagination = !!pageCount;

	return (
		<div className="sliding-pagination" style={{ width }}>
			{hasPagination && !disabled && page !== 0 &&
				<button
					className="sliding-pagination-button prev"
					onClick={handlePrevious}
					disabled={page === 0}
				>
					<span className="sliding-pagination-button-text">
						{previousButtonText}
					</span>
					<div className="sliding-pagination-button-icon" />
				</button>
			}

			{false && fadeOutSides && hasPagination &&
				<div className="sliding-pagination-fade left" />
			}

			<div className="sliding-pagination-inner">
				<div className="sliding-pagination-inner-content" style={innerStyles}>
					{children}
				</div>
			</div>

			{fadeOutSides && hasPagination && page !== pageCount &&
				<div className="sliding-pagination-fade right" />
			}

			{hasPagination && !disabled && page !== pageCount &&
				<button
					className="sliding-pagination-button next"
					onClick={handleNext}
					disabled={page === pageCount}
				>
					<span className="sliding-pagination-button-text">
						{nextButtonText}
					</span>
					<div className="sliding-pagination-button-icon" />
				</button>
			}
		</div>
	);
};

SlidingPagination.propTypes = {
	itemWidth: PropTypes.number.isRequired,
	itemGutter: PropTypes.number.isRequired,
	width: PropTypes.number.isRequired,
	previousButtonText: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
	]),
	nextButtonText: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
	]),
	children: PropTypes.node,
	fadeOutSides: PropTypes.bool,
	disabled: PropTypes.bool,
	onPrevious: PropTypes.func,
	onNext: PropTypes.func,
	page: PropTypes.number,
};

SlidingPagination.defaultProps = {
	previousButtonText: "Previous",
	nextButtonText: "Next",
	page: 0,
};

export default SlidingPagination;



// WEBPACK FOOTER //
// ./src/shared/components/SlidingPagination.jsx